import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import api from "@/config/api";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_test_51SgY9qSZIhz0iqBodCXaglqbrz66umV3ZKez7WB2BIPUVHMwu1dSAdUoO1YjMEJBVHMap08ItL0R174pe25fGrkG0091P8Kn2L"
);

function PaymentForm({ subscriptionId }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentIntent, setPaymentIntent] = useState(null);

  const createPaymentIntent = useCallback(async () => {
    try {
      const data = await api.subscriptions.createPaymentIntent(subscriptionId);
      setPaymentIntent(data);
    } catch (error) {
      console.error("Payment intent error:", error);
      if (
        error.message?.includes("not configured") ||
        error.message?.includes("STRIPE_SECRET_KEY")
      ) {
        setError(
          "Payment processing is not configured. Please contact support or check server configuration."
        );
      } else {
        setError(error.message || "Failed to initialize payment");
      }
    }
  }, [subscriptionId]);

  useEffect(() => {
    createPaymentIntent();
  }, [createPaymentIntent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent: confirmedPayment } =
        await stripe.confirmCardPayment(paymentIntent.client_secret, {
          payment_method: {
            card: cardElement,
          },
        });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Confirm payment with backend
      await api.subscriptions.confirmPayment(
        subscriptionId,
        confirmedPayment.id
      );

      // Navigate to dashboard - auth context will handle token refresh
      navigate("/dashboard");
    } catch (error) {
      setError(error.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!paymentIntent) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        <p className="mt-4 text-gray-600">Initializing payment...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-900">Amount:</span>
          <span className="text-2xl font-bold text-cyan-600">
            {paymentIntent.currency === "usd"
              ? "$"
              : paymentIntent.currency.toUpperCase()}
            {(paymentIntent.amount / 100).toFixed(2)}
          </span>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-cyan-600 text-white py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading
          ? "Processing Payment..."
          : `Pay ${paymentIntent.currency === "usd" ? "$" : ""}${(
              paymentIntent.amount / 100
            ).toFixed(2)}`}
      </button>

      <p className="text-sm text-gray-500 text-center">
        Your payment is secure and encrypted. We use Stripe for payment
        processing.
      </p>
    </form>
  );
}

export default function Payment() {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSubscription = useCallback(async () => {
    try {
      const subscriptions = await api.subscriptions.getMySubscriptions();
      const sub = subscriptions.find((s) => s.id === subscriptionId);
      if (sub) {
        setSubscription(sub);
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [navigate, subscriptionId]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Complete Your Payment
          </h2>
          <p className="text-gray-600 mb-8">Secure payment powered by Stripe</p>

          {subscription && (
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">Package:</span>
                <span className="font-semibold">
                  {subscription.package_name}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Billing:</span>
                <span className="font-semibold capitalize">
                  {subscription.billing_period}
                </span>
              </div>
            </div>
          )}

          <Elements stripe={stripePromise}>
            <PaymentForm subscriptionId={subscriptionId} />
          </Elements>
        </div>
      </div>
    </div>
  );
}
